<template>
  <ocs-data-loader :data-loaded="dataLoaded" :data-load-error="dataLoadError" :data-not-found="dataNotFound">
    <template v-slot:not-found>
      <not-found />
    </template>
    <b-row>
      <b-col>
        <b-breadcrumb class="bg-light">
          <template>
            <b-breadcrumb-item :to="{ name: 'apply' }">
              Calls for Proposals
            </b-breadcrumb-item>
            <b-breadcrumb-item active> Submit proposal </b-breadcrumb-item>
          </template>
        </b-breadcrumb>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="9" cols="12">
        <p>You are creating a proposal for {{ data.semester }}.</p>
        <template v-if="data.proposal_type !== 'DDT'">
          <p>
            Deadline: <strong>{{ data.deadline | formatDate }}</strong> ({{ data.deadline | timeFromNow }})
          </p>
        </template>
        <p>{{ data.eligibility }}</p>
        <b-alert v-for="error in apiValidationErrors.non_field_errors" :key="error" variant="danger" dismissible show> {{ error }}</b-alert>
        <b-alert v-for="error in apiValidationErrors.call_id" :key="error" variant="danger" dismissible show> {{ error }}</b-alert>
        <b-form>
          <basic-custom-field v-model="sciApp.title" :errors="apiValidationErrors.title" label="Title" placeholder="Title"></basic-custom-field>
          <basic-custom-field
            v-model="sciApp.abstract"
            :errors="apiValidationErrors.abstract"
            placeholder="Limit 500 words"
            label="Abstract"
            field="abstract"
            field-type="textarea"
            rows="8"
          ></basic-custom-field>
          <basic-custom-field
            v-model="isStudent"
            field-type="checkbox"
            field="is-student-tag"
            :label="''"
            :errors="apiValidationErrors.tags"
            @input="updateTags('student', isStudent)"
            checkbox-option-label="Data from this proposal will be an important component of a PhD thesis"
          ></basic-custom-field>
          <basic-custom-field
            v-model="isLongTerm"
            field-type="checkbox"
            field="is-long-term-tag"
            :label="''"
            :errors="apiValidationErrors.tags"
            @input="updateTags('long-term', isLongTerm)"
            checkbox-option-label="This is a long-term proposal and may be awarded time for up to 3 consecutive semesters."
          ></basic-custom-field>
          <basic-custom-field
            v-model="isOpenAccess"
            field-type="checkbox"
            field="is-open-access-tag"
            :label="''"
            :errors="apiValidationErrors.tags"
            @input="updateTags('open-access', isOpenAccess)"
            checkbox-option-label="This is part of the Open Access Program."
          ></basic-custom-field>
          <basic-custom-field
            v-model="isLCO"
            field-type="checkbox"
            field="lco-tag"
            :label="''"
            :errors="apiValidationErrors.tags"
            @input="updateTags('lco', isLCO)"
            checkbox-option-label="This is for LCO’s share of network time."
          ></basic-custom-field>
          <template v-if="data.proposal_type === 'COLAB'">
            <basic-custom-field
              v-model="sciApp.tac_rank"
              :errors="apiValidationErrors.tac_rank"
              label="Rank"
              field="tac-rank"
              type="number"
              min="0"
            ></basic-custom-field>
          </template>
          <template>
            <h5>Principal Investigator</h5>
            <div class="help-block">
              <strong>
                If you are the principal investigator on this proposal, please ensure your information is correct and up to date before proceeding.
                You can view and update your details on your <router-link :to="{ name: 'profile' }">profile</router-link> page.
              </strong>
            </div>
            <div class="help-block" v-if="data.proposal_type !== 'KEY'">
              The principle investigator will not be presented to reviewers.
            </div>
            <b-table-lite :items="[{}]" :fields="userTableFields">
              <template #cell(email)>
                <basic-custom-field
                  v-model="sciApp.pi"
                  :errors="apiValidationErrors.pi"
                  label="Email"
                  placeholder="Email"
                  field="pi"
                  type="email"
                  :label-sr-only="true"
                ></basic-custom-field>
              </template>
              <template #cell(firstName)>
                <basic-custom-field
                  v-model="sciApp.pi_first_name"
                  :errors="apiValidationErrors.pi_first_name"
                  label="First Name"
                  placeholder="First Name"
                  field="pi-first-name"
                  label-sr-only
                ></basic-custom-field>
              </template>
              <template #cell(lastName)>
                <basic-custom-field
                  v-model="sciApp.pi_last_name"
                  :errors="apiValidationErrors.pi_last_name"
                  label="Last Name"
                  placeholder="Last Name"
                  field="pi-last-name"
                  label-sr-only
                ></basic-custom-field>
              </template>
              <template #cell(institution)>
                <basic-custom-field
                  v-model="sciApp.pi_institution"
                  :errors="apiValidationErrors.pi_institution"
                  label="Institution"
                  placeholder="Institution"
                  field="pi-institution"
                  label-sr-only
                ></basic-custom-field>
              </template>
            </b-table-lite>
          </template>
          <h5>Co-investigators</h5>
          <div class="help-block">
            <!-- TODO: Translate this -->
            Add the details of co-investigators on this proposal. 
            <p v-if="data.proposal_type !== 'KEY'">Co-investigators will not be presented to reviewers.</p>
          </div>
          <b-alert v-for="error in coInvestigatorsNonFieldErrors" :key="error" variant="danger" dismissible show> {{ error }}</b-alert>
          <b-table-lite :items="sciApp.coinvestigator_set" :fields="userTableFields" class="mb-0">
            <template #cell(email)="data">
              <basic-custom-field
                v-model="sciApp.coinvestigator_set[data.index].email"
                :errors="getApiValidationError(['coinvestigator_set', data.index, 'email'])"
                label="Email"
                placeholder="Email"
                :field="'coi-email' + data.index"
                type="email"
                :label-sr-only="true"
              ></basic-custom-field>
            </template>
            <template #cell(firstName)="data">
              <basic-custom-field
                v-model="sciApp.coinvestigator_set[data.index].first_name"
                :errors="getApiValidationError(['coinvestigator_set', data.index, 'first_name'])"
                label="First Name"
                placeholder="First Name"
                :field="'coi-first-name' + data.index"
                :label-sr-only="true"
              ></basic-custom-field>
            </template>
            <template #cell(lastName)="data">
              <basic-custom-field
                v-model="sciApp.coinvestigator_set[data.index].last_name"
                :errors="getApiValidationError(['coinvestigator_set', data.index, 'last_name'])"
                label="Last Name"
                placeholder="Last Name"
                :field="'coi-last-name' + data.index"
                :label-sr-only="true"
              ></basic-custom-field>
            </template>
            <template #cell(institution)="data">
              <basic-custom-field
                v-model="sciApp.coinvestigator_set[data.index].institution"
                :errors="getApiValidationError(['coinvestigator_set', data.index, 'institution'])"
                label="Institution"
                placeholder="Institution"
                :field="'coi-institution' + data.index"
                :label-sr-only="true"
              ></basic-custom-field>
              <b-link class="float-right" href="#" title="Delete CoInvestigator" @click="removeCoInvestigator(data.index)">
                <span class="text-danger mx-auto"><i class="far fa-trash-alt"></i></span>
              </b-link>
            </template>
          </b-table-lite>
          <b-link href="#" @click="addCoInvestigator"><i class="fas fa-plus"></i> Add another</b-link>
          <br />
          <br />
          <template>
            <h5>Observing Budget</h5>
            <!-- TODO: Translate this block -->
            <div class="help-block">
              Requested observing time in hours for the semester. Time for
              <a href="https://lco.global/documentation/special-scheduling-modes/" target="_blank">Rapid Response mode</a>
              and
              <a href="https://lco.global/documentation/special-scheduling-modes/" target="_blank">Time-Critical mode</a> observations must be
              requested separately.
            </div>
            <b-alert v-for="error in timeRequestNonFieldErrors" :key="error" variant="danger" dismissible show> {{ error }}</b-alert>
            <b-table-lite :items="sciApp.timerequest_set" :fields="timeRequestFields" class="mb-0">
              <template #cell(semester)="data">
                <basic-custom-field
                  v-model="sciApp.timerequest_set[data.index].semester"
                  :errors="getApiValidationError(['timerequest_set', data.index, 'semester'])"
                  label="Semester"
                  :field="'timerequest-semester-' + data.index"
                  field-type="select"
                  :options="semesterOptions"
                  label-sr-only
                ></basic-custom-field>
              </template>
              <template #cell(instrumentTypes)="data">
                <basic-custom-field
                  v-model="sciApp.timerequest_set[data.index].instrument_types"
                  :errors="getApiValidationError(['timerequest_set', data.index, 'instrument_types'])"
                  label="Instrument"
                  :field="'timerequest-instrument-types-' + data.index"
                  field-type="multiselect"
                  :options="instrumentOptions"
                  label-sr-only
                ></basic-custom-field>
              </template>
              <template #cell(standardTime)="data">
                <basic-custom-field
                  v-model="sciApp.timerequest_set[data.index].std_time"
                  :errors="getApiValidationError(['timerequest_set', data.index, 'std_time'])"
                  label="Standard Time"
                  type="number"
                  :field="'timerequest-std-time' + data.index"
                  :label-sr-only="true"
                  min="0"
                ></basic-custom-field>
              </template>
              <template #cell(rapidResponse)="data">
                <basic-custom-field
                  v-model="sciApp.timerequest_set[data.index].rr_time"
                  :errors="getApiValidationError(['timerequest_set', data.index, 'rr_time'])"
                  label="Rapid Response Time"
                  type="number"
                  :field="'timerequest-rr-time' + data.index"
                  :label-sr-only="true"
                  min="0"
                ></basic-custom-field>
              </template>
              <template #cell(timeCritical)="data">
                <basic-custom-field
                  v-model="sciApp.timerequest_set[data.index].tc_time"
                  :errors="getApiValidationError(['timerequest_set', data.index, 'tc_time'])"
                  label="Time Critical Time"
                  type="number"
                  :field="'timerequest-tc-time' + data.index"
                  :label-sr-only="true"
                  min="0"
                ></basic-custom-field>
                <div class="container d-flex justify-content-end">
                <b-link class="mr-3" href="#" title="Copy time request" @click="copyTimeRequest(data.index)">
                  <span class="mx-auto"><i class="far fa-copy"></i></span>
                </b-link>
                <b-link href="#" title="Delete time request" @click="removeTimeRequest(data.index)">
                  <span class="text-danger mx-auto"><i class="far fa-trash-alt"></i></span>
                </b-link>
                </div>
              </template>
            </b-table-lite>
            <b-link href="#" @click="addTimeRequest"><i class="fas fa-plus"></i> Add another</b-link>
            <br />
            <br />
            <template v-if="data.proposal_type !== 'COLAB'">
              <h5>Upload PDF</h5>
              <div class="help-block">
                <template v-if="data.proposal_type === 'SCI'">
                  <p>
                    For the remainder of your proposal, please upload a single pdf file that includes the following sections
                    <em>but not an author list</em>:
                  </p>
                  <ul>
                    <li>
                      <strong>Science Justification.</strong> This section must provide appropriate background information and state the goals of the
                      project. The results of any previous time allocated for this project should be discussed. Pertinent references should be
                      included. Figures may be embedded or appended, but the text itself should be limited to approximately the equivalent of two
                      pages.
                    </li>
                    <li>
                      <strong>Experimental Design.</strong> This section should describe the strategy of your observing program, including the
                      characteristics of your targets, what measurements you will make from the data, and what additional work you will do to address
                      your science goals. If your targets are known, list them and their expected brightnesses. This section must include an
                      explanation of your observing budget, in which you justify the instruments, the exposure times, and the total number of hours
                      you’re requesting. You must provide independent justification for requests for Rapid Response or Time Critical observations. If
                      these are not adequately justified, they will not be granted. If unusual scheduling constraints might impact your success,
                      identify them.
                    </li>
                    <li>
                      <strong>Report of related programs on other telescopes.</strong> A concise account of other programs that relate to this
                      proposal.
                    </li>
                  </ul>
                  <p>The following two items must begin on a new page (within the same pdf):</p>
                  <ul>
                    <li>
                      <strong>Report on use of LCO in the past 3 years.</strong> A concise account of LCO network time used in the past 3 years.
                    </li>
                    <li><strong>List of the authors’ related publications. </strong>Up to 10 publications.</li>
                  </ul>
                  <p>The format of the pdf file is subject to the following constraints.</p>
                  <ul>
                    <li>The font size must be 11 points or larger.</li>
                    <li>Margins on all edges must be at least 1 inch.</li>
                    <li>Line spacing must be no denser than 6 lines per inch.</li>
                    <li>The file size must be under {{ pdfSizeLimit.asMegaBytes }}MB.</li>
                    <li>The recommended length is 7 pages.</li>
                  </ul>
                </template>
                <template v-else-if="data.proposal_type === 'KEY'">
                  <p>
                    For the remainder of your proposal, please upload a single pdf file. Consult the Call for Key Projects and the
                    <a href="https://lco.global/observatory/process/key-project-proposal-guidelines/"> Key project proposal guidelines</a>.
                  </p> 
                </template>
                <template v-else-if="data.proposal_type === 'DDT'">
                  <p>
                    For the remainder of your proposal, please upload a single pdf file that includes the following sections:
                  </p>
                  <ul>
                    <li>
                      <strong>Science Justification.</strong> This section must provide appropriate background information and state the goals of the
                      observations. Pertinent references should be included. Figures may be embedded or appended, but the text itself should be
                      limited to approximately the equivalent of a single page.
                    </li>
                    <li>
                      <strong>Experimental Design.</strong> This section should describe the characteristics of your targets and what measurements you
                      will make from the data. If your targets are known, list them and their expected brightnesses. This section must also justify
                      the instruments, the exposure times, and the total time you’re requesting. You must provide independent justification for
                      requests for Rapid Response or Time Critical observations. If these are not adequately justified, they will not be granted. If
                      unusual scheduling constraints might impact your success, identify them.
                    </li>
                    <li>
                      <strong>Discretionary Time Justification.</strong> Discretionary time is reserved for observations of unforeseen
                      targets-of-opportunity. This section should describe why the proposal must be reviewed now, rather than as part of the normal
                      time allocation process for the upcoming semester. Additionally, describe through what affiliation you are entitled to request
                      DD time.
                    </li>
                  </ul>
                  <p>The format of the pdf file is subject to the following constraints.</p>
                  <ul>
                    <li>The font size must be 11 points or larger.</li>
                    <li>Margins on all edges must be at least 1 inch.</li>
                    <li>Line spacing must be no denser than 6 lines per inch.</li>
                    <li>The file size must be under {{ pdfSizeLimit.asMegaBytes }}MB.</li>
                    <li>The recommended length is 2 pages</li>
                  </ul>
                </template>
                <template v-else>
                  <p>The format of the pdf file is subject to the following constraints.</p>
                  <ul>
                    <li>The file size must be under {{ pdfSizeLimit.asMegaBytes }}MB.</li>
                  </ul>
                </template>
              </div>
              <template v-if="pdfPath">
                Currently: <b-link :href="pdfPath" target="_blank">{{ pdfBaseName }}</b-link>
                <b-form-checkbox v-model="clearPdf" class="d-inline ml-1">Clear</b-form-checkbox>
                <b-alert v-for="error in apiValidationErrors.clear_pdf" :key="error" variant="danger" dismissible show> {{ error }}</b-alert>
                <div class="pt-2">Change:</div>
              </template>
              <basic-custom-field v-model="sciApp.pdf" field-type="file" :errors="pdfErrors" label="Upload PDF" accept=".pdf, .PDF" label-sr-only>
              </basic-custom-field>
            </template>
          </template>
        </b-form>
        <a href="#">Back to top</a>
      </b-col>
      <b-col md="3" cols="12">
        <!-- TODO: translate this -->
        <h3>
          Application status:
          {{ sciApp.status }}
        </h3>
        <!-- TODO: Translate these paragraphs -->
        <p>You may save your application and work on it later.</p>
        <p>Once you submit your application, you will no longer be able to edit it.</p>
        <!-- TODO: translate button text -->
        <b-button variant="primary" :disabled="isBusyPostingApplication || pdfIsTooLarge" class="mr-1" @click="saveApplication">
          Save
        </b-button>
        <b-button variant="success" :disabled="isBusyPostingApplication || pdfIsTooLarge" @click="confirm(confirmSubmitMessage, submitApplication)">
          Submit
        </b-button>
        <template v-if="updatingSciApp">
          <p>Previewing your submission will <strong>discard</strong> any pending edits.</p>
          <!-- TODO: translate button text  -->
          <p>
            <router-link :to="{ name: 'appDetail', params: { sciAppId: initialSciApp.id } }">
              <span class="mx-auto"><i class="fa fa-print"></i></span> Preview
            </router-link>
          </p>
          <!-- TODO: translate text  -->
          <p>Deleting a draft is an irreversible action.</p>
          <!-- TODO: translate button text  -->
          <p>
            <b-button variant="danger" :disabled="isBusyDeletingApplication" @click="confirm(confirmDeleteMessage, deleteApplication)">
              Delete
            </b-button>
          </p>
        </template>
      </b-col>
    </b-row>
  </ocs-data-loader>
</template>
<script>
import $ from 'jquery';
import _ from 'lodash';
import { OCSUtil, OCSMixin } from 'ocs-component-lib';

import BasicCustomField from '@/components/util/BasicCustomField.vue';
import NotFound from '@/components/NotFound.vue';

export default {
  name: 'EditSciApplication',
  components: {
    BasicCustomField,
    NotFound
  },
  filters: {
    formatDate: function(value) {
      return OCSUtil.formatDate(value, 'D MMM YYYY, h:mm a z');
    },
    timeFromNow: function(value) {
      return OCSUtil.timeFromNow(value);
    },
    lowerCase: function(value) {
      return _.lowerCase(value);
    }
  },
  mixins: [OCSMixin.getDataMixin, OCSMixin.confirmMixin],
  props: {
    callId: {
      type: [String, Number],
      required: true
    },
    initialSciApp: {
      type: Object,
      required: false,
      default: function() {
        return {};
      },
      description: 'The existing science application body for update. If not set, then a new application is being created.'
    }
  },
  data: function() {
    let sizeLimitAsMegaBytes = 10;
    const pdfSizeLimit = {
      asMegaBytes: sizeLimitAsMegaBytes,
      asBytes: sizeLimitAsMegaBytes * 1000000
    };
    let sciAppData = this.getSciAppData(this.callId, this.initialSciApp);
    return {
      sciApp: sciAppData.sciApp,
      pdfPath: sciAppData.pdfPath,
      isStudent: sciAppData.isStudent,
      isLongTerm: sciAppData.isLongTerm,
      isOpenAccess: sciAppData.isOpenAccess,
      isLCO: sciAppData.isLCO,
      clearPdf: false,
      userTableFields: ['email', 'firstName', 'lastName', 'institution'],
      timeRequestFields: [{ key: 'semester', class: 'd-none' }, 'instrumentTypes', 'standardTime', 'rapidResponse', 'timeCritical'],
      confirmSubmitMessage: 'Submit this application? You will no longer be able to edit it.',
      confirmDeleteMessage: 'Are you sure you want to delete "' + _.get(this.initialSciApp, 'title', 'this application') + '"?',
      isBusyPostingApplication: false,
      isBusyDeletingApplication: false,
      pdfSizeLimit: pdfSizeLimit,
      apiValidationErrors: {},
      pdfIsTooLarge: false
    };
  },
  computed: {
    updatingSciApp: function() {
      return this.initialSciApp.id ? true : false;
    },
    semesterOptions: function() {
      let options = [{ value: '', text: '--------' }];
      for (let semesterId of _.get(this.data, 'eligible_semesters', [])) {
        options.push({ value: semesterId, text: semesterId });
      }
      return options;
    },
    numberOfEligibleSemesters: function() {
      return _.get(this.data, 'eligible_semesters', []).length;
    },
    instrumentOptions: function() {
      let options = [{ value: '', text: '--------' }];
      for (let instrument of _.get(this.data, 'instruments', [])) {
        options.push({ value: instrument.id, text: instrument.name });
      }
      return options;
    },
    pdfBaseName: function() {
      let lastElementofPath = _.last(_.split(this.pdfPath, '/'));
      let lastElementWithoutQueryString = _.head(_.split(lastElementofPath, '?'));
      return lastElementWithoutQueryString || this.pdfPath;
    },
    pdfErrors: function() {
      let errors = [];
      if (this.apiValidationErrors.pdf) {
        for (let pdfApiValidationError of this.apiValidationErrors.pdf) {
          errors.push(pdfApiValidationError);
        }
      }
      if (this.pdfIsTooLarge) {
        errors.push('PDF is too large');
      }
      return errors;
    },
    timeRequestNonFieldErrors: function() {
      return _.get(this.apiValidationErrors, ['timerequest_set', 'non_field_errors'], []);
    },
    coInvestigatorsNonFieldErrors: function() {
      return _.get(this.apiValidationErrors, ['coinvestigator_set', 'non_field_errors'], []);
    },
    observationPortalApiUrl: function() {
      return this.$store.state.urls.observationPortalApi;
    }
  },
  watch: {
    'sciApp.pdf': function(pdf) {
      if (_.get(pdf, 'size', 0) > this.pdfSizeLimit.asBytes) {
        this.pdfIsTooLarge = true;
      } else {
        this.pdfIsTooLarge = false;
      }
    },
    numberOfEligibleSemesters: function(value) {
      // The number of eligible semesters should always be 1 or more. If there is more than one, the
      // dropdown with all the semester options should be displayed for the user.
      if (value > 1) {
        for (let field of this.timeRequestFields) {
          if (field.key === 'semester') {
            field.class = '';
          }
        }
      }
    },
    data: function(data) {
      // Fill in pi fields with submitter details since that is the most common use case for
      // all call types, except science collaboration calls.
      if (_.get(data, 'proposal_type', '') !== 'COLAB' && !this.updatingSciApp) {
        this.sciApp.pi = this.$store.state.profile.email;
        this.sciApp.pi_first_name = this.$store.state.profile.first_name;
        this.sciApp.pi_last_name = this.$store.state.profile.last_name;
        this.sciApp.pi_institution = this.$store.state.profile.profile.institution;
      }
    }
  },
  methods: {
    initializeDataEndpoint: function() {
      return this.$store.state.urls.observationPortalApi + '/api/calls/' + this.callId + '/';
    },
    getSciAppData: function(callId, initialSciApp) {
      // Return sciApp data. If `initialSciApp` is passed in, the sciApp data that
      // is returned is generated it, otherwise sciApp fields are set to defaults.
      initialSciApp = initialSciApp || {};
      let sciApp = {
        call: callId,
        title: initialSciApp.title || '',
        // The 'UNSAVED' state is not a valid state when submitting to the API, it
        // is set here as feedback to the user
        status: initialSciApp.status || 'UNSAVED',
        abstract: initialSciApp.abstract || '',
        tac_rank: initialSciApp.tac_rank || 0,
        pi: initialSciApp.pi || '',
        pi_first_name: initialSciApp.pi_first_name || '',
        pi_last_name: initialSciApp.pi_last_name || '',
        pi_institution: initialSciApp.pi_institution || '',
        coinvestigator_set: initialSciApp.coinvestigator_set || [],
        timerequest_set: initialSciApp.timerequest_set || [],
        tags: initialSciApp.tags || [],
        pdf: null
      };
      let isStudent = false;
      if (sciApp.tags.includes('student')) {
        isStudent = true;
      }
      let isLongTerm = false;
      if (sciApp.tags.includes('long-term')) {
        isLongTerm = true;
      }
      let isOpenAccess = false;
      if (sciApp.tags.includes('open-access')) {
        isOpenAccess = true;
      }
      let isLCO = false;
      if (sciApp.tags.includes('lco')) {
        isLCO = true;
      }
      if (_.get(sciApp, 'timerequest_set', []).length === 0) {
        sciApp.timerequest_set = [this.getEmptyTimeRequest()];
      }
      if (_.get(sciApp, 'coinvestigator_set', []).length === 0) {
        sciApp.coinvestigator_set = [this.getEmptyCoInvestigator()];
      }
      // `pdfPath` will either be null for a new science application or
      // for an application to be updated where no pdf has been uploaded yet,
      // or it will be the path to an uploaded pdf.
      return { sciApp: sciApp, pdfPath: initialSciApp.pdf || null, isStudent: isStudent, isLongTerm: isLongTerm, isOpenAccess: isOpenAccess, isLCO: isLCO };
    },
    updateTags: function(newTag, checked) {
      checked ? this.sciApp.tags.push(newTag) : _.pull(this.sciApp.tags, newTag);
    },
    getEmptyTimeRequest: function() {
      return { semester: '', instrument_types: [], std_time: 0, rr_time: 0, tc_time: 0 };
    },
    getEmptyCoInvestigator: function() {
      return { email: '', first_name: '', last_name: '', institution: '' };
    },
    getApiValidationError: function(path) {
      return _.get(this.apiValidationErrors, path, null);
    },
    removeCoInvestigator: function(index) {
      if (index >= 0) {
        this.sciApp.coinvestigator_set.splice(index, 1);
      }
    },
    addCoInvestigator: function() {
      this.sciApp.coinvestigator_set.push(OCSUtil.copyObject(this.getEmptyCoInvestigator()));
    },
    removeTimeRequest: function(index) {
      if (index >= 0) {
        this.sciApp.timerequest_set.splice(index, 1);
      }
    },
    copyTimeRequest: function(index) {
      if (index >= 0) {
        this.sciApp.timerequest_set.push(OCSUtil.copyObject(this.sciApp.timerequest_set.at(index)));
      }
    },
    addTimeRequest: function() {
      this.sciApp.timerequest_set.push(OCSUtil.copyObject(this.getEmptyTimeRequest()));
    },
    sendApplication(data) {
      let method = 'POST';
      let url = this.observationPortalApiUrl + '/api/scienceapplications/';
      if (this.updatingSciApp) {
        method = 'PUT';
        url = this.observationPortalApiUrl + '/api/scienceapplications/' + this.initialSciApp.id + '/';
      }
      let that = this;
      this.isBusyPostingApplication = true;
      this.$store.commit('clearAllMessages');
      $.ajax({
        method: method,
        url: url,
        data: data,
        cache: false,
        contentType: false,
        processData: false
      })
        .done(function(response) {
          that.apiValidationErrors = {};
          if (data.get('status') === 'DRAFT' && that.updatingSciApp) {
            let sciAppData = that.getSciAppData(that.callId, response);
            that.pdfPath = sciAppData.pdfPath;
            that.sciApp = sciAppData.sciApp;
            that.clearPdf = false;
            that.$store.commit('addMessage', { text: 'Application saved.', variant: 'success' });
          } else if (data.get('status') === 'DRAFT' && !that.updatingSciApp) {
            that.$store.commit('addMessage', { text: 'Application created.', variant: 'success' });
            that.$router.replace({ name: 'updateApp', params: { sciAppId: response.id, persistMessage: true } });
          } else {
            that.$store.commit('addMessage', { text: 'Application successfully submitted.', variant: 'success' });
            that.$router.replace({ name: 'apply', params: { persistMessage: true } });
          }
        })
        .fail(function(response) {
          if (response.status === 400) {
            that.$store.commit('addMessage', { text: 'There was an error with your submission.', variant: 'danger' });
            that.apiValidationErrors = response.responseJSON;
          } else if (response.status === 404) {
            that.$store.commit('addMessage', { text: 'Either the application can not be updated, or the call has closed.', variant: 'danger' });
          } else {
            that.$store.commit('addMessage', {
              text: 'There was an unexpected error sending your application, please try again later.',
              variant: 'danger'
            });
          }
        })
        .always(function() {
          that.isBusyPostingApplication = false;
        });
    },
    filterForRowsWithData: function(fields, rows, emptyValues) {
      emptyValues = emptyValues || [];
      let rowsWithData = [];
      for (let row of rows) {
        let hasData = false;
        for (let field of fields) {
          if (row[field] && emptyValues.indexOf(row[field]) < 0) {
            hasData = true;
            break;
          }
        }
        if (hasData) {
          rowsWithData.push(OCSUtil.copyObject(row));
        }
      }
      return rowsWithData;
    },
    getTimeRequestsForSubmissionBody: function() {
      // Only time requests that are at least partially filled out should be included in
      // the submission body. If the semester options dropdown is hidden, do not include
      // the semester field in the list of fields to check for data since a user will not
      // be able to update that field anyway - in this case, automatically set the semester
      // since there is only one viable option.
      let emptyValues = ['0'];
      let checkFieldsForData = ['instrument_types', 'std_time', 'rr_time', 'tc_time'];
      if (this.numberOfEligibleSemesters > 1) {
        checkFieldsForData.push('semester');
      }
      let timeRequests = this.filterForRowsWithData(checkFieldsForData, this.sciApp.timerequest_set, emptyValues);
      for (let timeRequest of timeRequests) {
        if (!timeRequest.semester && this.numberOfEligibleSemesters === 1) {
          timeRequest.semester = this.data.eligible_semesters[0];
        }
      }
      return timeRequests;
    },
    getCoInvestigatorsForSubmissionBody: function() {
      // Only CoIs that are at least partially filled out should be included in
      // the submission body.
      let checkFieldsForData = ['email', 'first_name', 'last_name', 'institution'];
      return this.filterForRowsWithData(checkFieldsForData, this.sciApp.coinvestigator_set);
    },
    getApplicationSubmissionBody: function(status) {
      var formData = new FormData();
      formData.append('status', status);
      formData.append('title', this.sciApp.title);
      formData.append('call_id', this.callId);
      formData.append('abstract', this.sciApp.abstract);
      formData.append('pi', this.sciApp.pi);
      formData.append('pi_first_name', this.sciApp.pi_first_name);
      formData.append('pi_last_name', this.sciApp.pi_last_name);
      formData.append('pi_institution', this.sciApp.pi_institution);
      formData.append('tac_rank', this.sciApp.tac_rank);

      let coInvestigators = this.getCoInvestigatorsForSubmissionBody();
      let timeRequests = this.getTimeRequestsForSubmissionBody();
      for (let i in timeRequests) {
        if (timeRequests[i].instrument_types.filter(x => !!x).length == 0) {
          continue;
        }
        formData.append('timerequest_set[' + i + ']instrument_types', timeRequests[i].instrument_types);
        formData.append('timerequest_set[' + i + ']semester', timeRequests[i].semester);
        formData.append('timerequest_set[' + i + ']std_time', timeRequests[i].std_time);
        formData.append('timerequest_set[' + i + ']rr_time', timeRequests[i].rr_time);
        formData.append('timerequest_set[' + i + ']tc_time', timeRequests[i].tc_time);
      }

      for (let i in coInvestigators) {
        formData.append('coinvestigator_set[' + i + ']email', coInvestigators[i].email || '');
        formData.append('coinvestigator_set[' + i + ']first_name', coInvestigators[i].first_name || '');
        formData.append('coinvestigator_set[' + i + ']last_name', coInvestigators[i].last_name || '');
        formData.append('coinvestigator_set[' + i + ']institution', coInvestigators[i].institution || '');
      }
      let tags = _.get(this.sciApp, 'tags', []);
      for (let tag of tags) {
        formData.append('tags', tag);
      }
      // Add pdf fields
      if (this.sciApp.pdf) {
        formData.append('pdf', this.sciApp.pdf);
      }
      if (this.clearPdf) {
        formData.append('clear_pdf', true);
      }
      return formData;
    },
    saveApplication: function() {
      this.sendApplication(this.getApplicationSubmissionBody('DRAFT'));
    },
    submitApplication: function() {
      this.sendApplication(this.getApplicationSubmissionBody('SUBMITTED'));
    },
    deleteApplication: function() {
      if (this.updatingSciApp) {
        this.$store.commit('clearAllMessages');
        this.isBusyDeletingApplication = true;
        let that = this;
        $.ajax({
          url: this.observationPortalApiUrl + '/api/scienceapplications/' + this.initialSciApp.id + '/',
          method: 'DELETE'
        })
          .done(function() {
            that.$store.commit('addMessage', { text: 'Application deleted.', variant: 'success' });
            that.$router.replace({ name: 'apply', params: { persistMessage: true } });
          })
          .fail(function() {
            that.$store.commit('addMessage', {
              text: 'There was a problem deleting the application, please try again.',
              variant: 'danger'
            });
          })
          .always(function() {
            that.isBusyDeletingApplication = false;
          });
      }
    }
  }
};
</script>
